import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Check, ArrowRight, TrendingUp, Users, Package, Megaphone, MapPin, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { trackPageView, trackFormSubmit } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const benefits = [
  { icon: TrendingUp, title: 'High Margins', description: 'Industry-leading profit margins that grow with your business.', color: '#f36458' },
  { icon: Users, title: 'Brand Trust', description: '20+ years of market presence and customer loyalty.', color: '#3b82f6' },
  { icon: Package, title: 'Product Range', description: '9 products across cement, blocks, and chemicals.', color: '#10b981' },
  { icon: Megaphone, title: 'Marketing Support', description: 'Promotional materials and co-marketing opportunities.', color: '#f59e0b' },
  { icon: MapPin, title: 'Logistics', description: 'Strong delivery network for timely supplies.', color: '#8b5cf6' },
];

const products = [
  'Lion Composite Cement', 'Lion Supreme', 'Hathi Cement', 'Siddhee Cement',
  'Cemtite Tile Adhesive', 'Cemtite Ready Mix Plaster', 'Cemtite Block Adhesive',
  'EFCON AAC Blocks', 'Cemtite GGBS', 'Cemtite Fly Ash'
];

const DistributorsPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '', company_name: '', email: '', phone: '',
    district: '', state: '', product_interest: [], message: '', promo_code: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  useEffect(() => {
    trackPageView('/distributors', 'Distributors');
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductToggle = (product) => {
    setFormData(prev => ({
      ...prev,
      product_interest: prev.product_interest.includes(product)
        ? prev.product_interest.filter(p => p !== product)
        : [...prev.product_interest, product]
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1: return formData.name && formData.company_name && formData.email && formData.phone;
      case 2: return formData.district && formData.state;
      case 3: return formData.product_interest.length > 0;
      default: return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handlePrev = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/distributor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Application submitted! We\'ll contact you soon.');
        trackFormSubmit('distributor_inquiry', { products: formData.product_interest.length });
        setFormData({
          name: '', company_name: '', email: '', phone: '',
          district: '', state: '', product_interest: [], message: '', promo_code: '',
        });
        setCurrentStep(1);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-void" data-testid="distributors-page">
      <SEOHead
        title="Become a Distributor"
        description="Join Avi Cement's 750+ dealer network across Gujarat and Rajasthan. Competitive margins, reliable supply, and dedicated support for distributors."
        path="/distributors"
      />
      {/* Hero Section */}
      <section className="relative py-12 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <motion.div
          className="absolute top-1/3 left-0 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(243,100,88,0.1) 0%, transparent 60%)' }}
        />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-coral font-mono text-sm mb-4 block">PARTNER WITH US</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Grow your business<br />
              <span className="text-gradient">with us</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Join our network of 500+ successful dealers. Partner with a brand that's been building India for 20+ years.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <a href="#apply-form" className="flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-6 py-3 rounded-full transition-all group">
                Quick Application <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link to="/dealer-form" className="flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-white font-display font-medium px-6 py-3 rounded-full transition-all" data-testid="dealer-form-link">
                Full Dealer Form
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={ref} className="py-12 md:py-32 bg-surface">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <span className="text-coral font-mono text-sm mb-4 block">WHY PARTNER WITH US</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white">
              Benefits of partnership
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1 }}
                className="bg-void border border-white/5 p-6 rounded-2xl text-center hover:border-white/20 transition-colors"
              >
                <div 
                  className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: `${benefit.color}15` }}
                >
                  <benefit.icon className="w-6 h-6" style={{ color: benefit.color }} />
                </div>
                <h3 className="font-display font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-500 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 md:py-32 bg-void" data-testid="distributor-form-section" id="apply-form">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="text-center mb-12">
              <span className="text-coral font-mono text-sm mb-4 block">APPLICATION FORM</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white">
                Become a distributor
              </h2>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-12">
              {['Contact', 'Location', 'Products', 'Review'].map((step, index) => (
                <div key={step} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-display text-sm transition-colors ${
                    currentStep > index + 1 ? 'bg-mint text-void' 
                      : currentStep === index + 1 ? 'bg-coral text-white' 
                      : 'bg-surface-light text-gray-500'
                  }`}>
                    {currentStep > index + 1 ? <Check className="w-5 h-5" /> : index + 1}
                  </div>
                  <span className={`hidden sm:block ml-2 text-sm ${currentStep >= index + 1 ? 'text-white' : 'text-gray-500'}`}>
                    {step}
                  </span>
                  {index < 3 && <div className={`w-8 sm:w-16 h-px mx-2 ${currentStep > index + 1 ? 'bg-mint' : 'bg-surface-light'}`} />}
                </div>
              ))}
            </div>

            {/* Form Steps */}
            <div className="bg-surface border border-white/5 rounded-2xl p-8">
              {currentStep === 1 && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                  <h3 className="font-display text-xl text-white mb-6">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input type="text" name="name" placeholder="Your Name *" value={formData.name} onChange={handleInputChange}
                      className="bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none" />
                    <input type="text" name="company_name" placeholder="Company Name *" value={formData.company_name} onChange={handleInputChange}
                      className="bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input type="email" name="email" placeholder="Email *" value={formData.email} onChange={handleInputChange}
                      className="bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none" />
                    <input type="tel" name="phone" placeholder="Phone *" value={formData.phone} onChange={handleInputChange}
                      className="bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none" />
                  </div>
                </motion.div>
              )}

              {currentStep === 2 && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                  <h3 className="font-display text-xl text-white mb-6">Location Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input type="text" name="district" placeholder="District *" value={formData.district} onChange={handleInputChange}
                      className="bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none" />
                    <input type="text" name="state" placeholder="State *" value={formData.state} onChange={handleInputChange}
                      className="bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none" />
                  </div>
                </motion.div>
              )}

              {currentStep === 3 && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                  <h3 className="font-display text-xl text-white mb-6">Products of Interest</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {products.map((product) => (
                      <button key={product} type="button" onClick={() => handleProductToggle(product)}
                        className={`p-4 rounded-xl text-left transition-colors ${
                          formData.product_interest.includes(product)
                            ? 'bg-coral text-white' : 'bg-void border border-white/10 text-gray-300 hover:border-white/30'
                        }`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                            formData.product_interest.includes(product) ? 'border-white bg-white' : 'border-gray-500'
                          }`}>
                            {formData.product_interest.includes(product) && <Check className="w-3 h-3 text-coral" />}
                          </div>
                          {product}
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {currentStep === 4 && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                  <h3 className="font-display text-xl text-white mb-6">Review & Submit</h3>
                  <div className="bg-void rounded-xl p-4 space-y-2 text-sm">
                    <p className="text-gray-400"><span className="text-white">Name:</span> {formData.name}</p>
                    <p className="text-gray-400"><span className="text-white">Company:</span> {formData.company_name}</p>
                    <p className="text-gray-400"><span className="text-white">Email:</span> {formData.email}</p>
                    <p className="text-gray-400"><span className="text-white">Location:</span> {formData.district}, {formData.state}</p>
                    <p className="text-gray-400"><span className="text-white">Products:</span> {formData.product_interest.join(', ')}</p>
                  </div>
                  <textarea name="message" placeholder="Additional Message (Optional)" value={formData.message} onChange={handleInputChange} rows={3}
                    className="w-full bg-void border border-white/10 focus:border-coral rounded-xl px-4 py-4 text-white placeholder:text-gray-500 outline-none resize-none" />
                  <input type="text" name="promo_code" placeholder="Promo Code (if any)" value={formData.promo_code} onChange={handleInputChange}
                    className="w-full bg-void border border-white/5 focus:border-coral rounded-xl px-4 py-3 text-white placeholder:text-gray-600 outline-none text-sm" />
                </motion.div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                {currentStep > 1 ? (
                  <button onClick={handlePrev} className="flex items-center gap-2 text-gray-400 hover:text-white font-display transition-colors">
                    <ChevronLeft className="w-5 h-5" /> Previous
                  </button>
                ) : <div />}
                {currentStep < 4 ? (
                  <button onClick={handleNext} className="flex items-center gap-2 bg-coral hover:bg-coral-dark text-white font-display px-6 py-3 rounded-full transition-colors">
                    Next <ChevronRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button onClick={handleSubmit} disabled={isSubmitting} className="flex items-center gap-2 bg-coral hover:bg-coral-dark text-white font-display px-6 py-3 rounded-full transition-colors disabled:opacity-50">
                    {isSubmitting ? 'Submitting...' : 'Submit Application'} <ArrowRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DistributorsPage;
